import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, CheckCircle2, ExternalLink, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import MediaLibrary from './MediaLibrary';

/**
 * Campo upload con:
 * - Drag & drop (singolo o MULTIPLO)
 * - Click per selezionare file da PC
 * - Incolla URL
 * - Pulsante "Scegli dalla libreria" (media già caricati)
 *
 * Modalità multiple:
 *   <UploadField multiple onMultiUpload={(urls) => addImages(urls)} />
 *   onMultiUpload riceve array di URL e si chiama UNA sola volta dopo tutti gli upload.
 *   In multi mode il `value` non viene mostrato come anteprima singola.
 */
export default function UploadField({ label, accept, folder = 'misc', value, onChange, multiple = false, onMultiUpload }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [showLib, setShowLib] = useState(false);
  const fileRef = useRef(null);

  // Salva URL RELATIVO al dominio (non assoluto) così è portabile tra domini
  // e non rimane bloccato su un vecchio host. Il client API normalizza `uploads/...`
  // in `/api/uploads/...` automaticamente in fase di lettura.
  const absUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    if (rawUrl.startsWith('http')) return rawUrl;
    if (rawUrl.startsWith('/')) return rawUrl;
    if (rawUrl.startsWith('uploads/')) return '/api/' + rawUrl;
    if (rawUrl.startsWith('api/uploads/')) return '/' + rawUrl;
    return `/api/${rawUrl}`;
  };

  const uploadOne = async (file) => {
    const res = await api.uploadFile(file, folder);
    return absUrl(res.url);
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setBusy(true);
    if (multiple) {
      setProgress({ done: 0, total: files.length });
      const urls = [];
      for (let i = 0; i < files.length; i += 1) {
        try {
          const u = await uploadOne(files[i]);
          urls.push(u);
        } catch (e) {
          toast.error(`Errore caricando ${files[i].name}: ${e?.message || 'errore'}`);
        }
        setProgress({ done: i + 1, total: files.length });
      }
      if (urls.length > 0 && onMultiUpload) onMultiUpload(urls);
      toast.success(`${urls.length}/${files.length} file caricati`);
      setProgress({ done: 0, total: 0 });
    } else {
      try {
        const u = await uploadOne(files[0]);
        onChange(u);
        toast.success('File caricato');
      } catch (e) {
        toast.error(e?.message || 'Errore upload');
      }
    }
    setBusy(false);
  };

  const onPick = (e) => { handleFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  const isImage = value && /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(value);
  const isVideo = value && /\.(mp4|webm)$/i.test(value);
  const isPdf = value && /\.pdf$/i.test(value);

  return (
    <div className="space-y-2">
      {label && <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest block">{label}</label>}

      {!multiple && value && (
        <div className="bg-navy-dark border border-white/10 rounded-lg p-3 flex items-center gap-3">
          {isImage && <img src={value} alt="" className="w-16 h-16 object-cover rounded" />}
          {isVideo && <video src={value} className="w-16 h-16 object-cover rounded bg-black" />}
          {isPdf && <div className="w-16 h-16 bg-red-500/20 border border-red-500/40 rounded flex items-center justify-center text-red-400 text-xs font-bold">PDF</div>}
          {!isImage && !isVideo && !isPdf && <div className="w-16 h-16 bg-navy rounded flex items-center justify-center"><CheckCircle2 size={20} className="text-green-400" /></div>}
          <div className="flex-1 min-w-0">
            <div className="text-white text-xs font-semibold truncate">{value.split('/').pop()}</div>
            <a href={value} target="_blank" rel="noreferrer" className="text-pink-brand text-xs inline-flex items-center gap-1"><ExternalLink size={11} /> Apri</a>
          </div>
          <button type="button" onClick={() => onChange('')} className="p-2 text-white/60 hover:text-red-400" title="Rimuovi"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-2">
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => !busy && fileRef.current?.click()}
          className={`col-span-8 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${busy ? 'border-pink-brand bg-pink-brand/10' : 'border-white/20 hover:border-pink-brand bg-navy-dark/40'}`}
        >
          {busy ? (
            <div className="text-pink-brand text-xs font-semibold inline-flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" />
              {progress.total > 1 ? `Caricamento ${progress.done}/${progress.total}…` : 'Caricamento…'}
            </div>
          ) : (
            <>
              <Upload size={16} className="text-pink-brand mx-auto mb-1" />
              <div className="text-white/80 text-[11px]">{multiple ? 'Trascina più file o clicca per caricarli' : 'Trascina o clicca per caricare'}</div>
              <div className="text-white/40 text-[10px] mt-0.5">{accept || 'qualsiasi tipo'}{multiple ? ' · selezione multipla' : ''}</div>
            </>
          )}
          <input ref={fileRef} type="file" accept={accept} multiple={multiple} onChange={onPick} className="hidden" />
        </div>
        <button type="button" onClick={() => setShowLib(true)} className="col-span-4 bg-navy-dark border border-white/20 rounded-lg p-3 text-white hover:border-pink-brand text-[11px] font-semibold inline-flex items-center justify-center gap-1">
          <FolderOpen size={14} /> {multiple ? 'Aggiungi dalla libreria' : 'Scegli dalla libreria'}
        </button>
      </div>

      {!multiple && (
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="… oppure incolla qui un URL diretto"
          className="w-full bg-navy-dark border border-white/15 rounded-lg px-3 py-1.5 text-white text-xs"
        />
      )}

      {showLib && (
        <MediaLibrary
          accept={accept}
          onPick={(url) => {
            if (multiple && onMultiUpload) onMultiUpload([url]);
            else onChange(url);
            setShowLib(false);
          }}
          onClose={() => setShowLib(false)}
        />
      )}
    </div>
  );
}
